import { create } from 'zustand';
import type { Prompt } from '../types';
import { captureSessionEpoch, isSessionEpochCurrent } from '../platform/sessionEpoch';

interface PromptState {
  prompts: Prompt[];
  isLoading: boolean;
  loadPrompts: () => Promise<void>;
  addPrompt: (prompt: Partial<Prompt>) => Promise<Prompt>;
  updatePrompt: (id: string, updates: Partial<Prompt>) => Promise<void>;
  deletePrompt: (id: string) => Promise<void>;
  reset: () => void;
}

let promptLoadSequence = 0;
let promptEpoch = 0;

function capturePromptEpoch() {
  return { store: promptEpoch, session: captureSessionEpoch() };
}

function isPromptEpochCurrent(epoch: ReturnType<typeof capturePromptEpoch>): boolean {
  return epoch.store === promptEpoch && isSessionEpochCurrent(epoch.session);
}

export const usePromptStore = create<PromptState>((set) => ({
  prompts: [],
  isLoading: false,

  loadPrompts: async () => {
    const epoch = capturePromptEpoch();
    const requestSequence = ++promptLoadSequence;
    set({ isLoading: true });
    try {
      const prompts = await window.electron.invoke(window.IPC_CHANNELS.PROMPT_LIST);
      if (requestSequence !== promptLoadSequence || !isPromptEpochCurrent(epoch)) return;
      set({ prompts, isLoading: false });
    } catch (error) {
      console.error('Failed to load prompts:', error);
      if (requestSequence === promptLoadSequence && isPromptEpochCurrent(epoch)) set({ isLoading: false });
    }
  },

  addPrompt: async (prompt) => {
    const epoch = capturePromptEpoch();
    const newPrompt = await window.electron.invoke(window.IPC_CHANNELS.PROMPT_CREATE, { prompt });
    if (isPromptEpochCurrent(epoch)) set((state) => ({ prompts: [newPrompt, ...state.prompts] }));
    return newPrompt;
  },

  updatePrompt: async (id, updates) => {
    const epoch = capturePromptEpoch();
    await window.electron.invoke(window.IPC_CHANNELS.PROMPT_UPDATE, { id, updates });
    if (!isPromptEpochCurrent(epoch)) return;
    set((state) => ({
      prompts: state.prompts.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    }));
  },

  deletePrompt: async (id) => {
    const epoch = capturePromptEpoch();
    await window.electron.invoke(window.IPC_CHANNELS.PROMPT_DELETE, { id });
    if (!isPromptEpochCurrent(epoch)) return;
    set((state) => ({
      prompts: state.prompts.filter((p) => p.id !== id),
    }));
  },

  reset: () => {
    ++promptEpoch;
    ++promptLoadSequence;
    set({ prompts: [], isLoading: false });
  },
}));
